import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PlayerAuctionService } from '../player-auction.service';
import { ContractAccount, ContractAccountStatus, CONTRACT_ACCOUNT_STATUS_OPTIONS, UpdateContractAccountRequest } from '../player-auction.type';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { AccountFormDialog } from './account-form-dialog/account-form-dialog';

@Component({
  selector: 'app-mule-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './mule-dialog.html',
  styleUrl: './mule-dialog.scss'
})
export class MuleDialog implements OnInit {
  displayedColumns = ['name', 'email', 'status', 'locked_by', 'created_at', 'action'];

  accounts: ContractAccount[] = [];
  loading = false;
  pageIndex = 0;
  pageSize = 10;
  total = 0;
  totalPages = 0;

  statusOptions: ContractAccountStatus[] = CONTRACT_ACCOUNT_STATUS_OPTIONS;
  updatingStatusId: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<MuleDialog>,
    private dialog: MatDialog,
    private service: PlayerAuctionService,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.loadAccounts();
  }

  loadAccounts(): void {
    this.loading = true;
    this.service.getContractAccounts({ pageIndex: this.pageIndex, pageSize: this.pageSize }).subscribe({
      next: res => {
        this.accounts = res.items;
        this.total = res.total;
        this.totalPages = res.totalPages;
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Error loading accounts', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  pageChanged(page: number): void {
    this.pageIndex = page;
    this.loadAccounts();
  }

  onPageInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const p = parseInt(input.value, 10);
    if (isNaN(p) || p < 1 || p > this.totalPages) {
      input.value = (this.pageIndex + 1).toString();
      return;
    }
    if (p - 1 !== this.pageIndex) this.pageChanged(p - 1);
  }

  openAddAccountDialog(): void {
    const ref = this.dialog.open(AccountFormDialog, { data: {} });
    ref.afterClosed().subscribe(result => {
      if (result) {
        this.pageIndex = 0;
        this.loadAccounts();
      }
    });
  }

  openEditAccountDialog(account: ContractAccount): void {
    const ref = this.dialog.open(AccountFormDialog, { data: { account } });
    ref.afterClosed().subscribe(result => {
      if (result) this.loadAccounts();
    });
  }

  onStatusChange(account: ContractAccount, newStatus: ContractAccountStatus): void {
    if (account.status === 'Locked') {
      this.snackBar.open('Locked accounts cannot have their status changed', 'Close', { duration: 4000 });
      return;
    }
    this.updatingStatusId = account.id;
    const request: UpdateContractAccountRequest = { status: newStatus };
    this.service.updateContractAccount(account.id, request).subscribe({
      next: () => {
        account.status = newStatus;
        this.snackBar.open('Status updated', 'Close', { duration: 3000 });
        this.updatingStatusId = null;
      },
      error: (err: { error?: { error?: string } }) => {
        const msg = err.error?.error || 'Error updating status';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
        this.updatingStatusId = null;
        this.loadAccounts();
      }
    });
  }

  deleteAccount(account: ContractAccount): void {
    if (account.status === 'Locked') {
      this.snackBar.open('Locked accounts cannot be deleted', 'Close', { duration: 4000 });
      return;
    }
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Delete Account',
        message: `Delete account "${account.login_info_email}"? This cannot be undone.`
      }
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.deleteContractAccount(account.id).subscribe({
        next: () => {
          this.snackBar.open('Account deleted', 'Close', { duration: 3000 });
          this.loadAccounts();
        },
        error: (err: { error?: { error?: string } }) => {
          const msg = err.error?.error || 'Error deleting account';
          this.snackBar.open(msg, 'Close', { duration: 5000 });
        }
      });
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  formatDate(d: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString();
  }

  getStatusClass(status: ContractAccountStatus): string {
    switch (status) {
      case 'Available': return 'status-available';
      case 'Locked': return 'status-locked';
      case 'Deactivated': return 'status-deactivated';
    }
  }
}
